import type { Replayer } from "rrweb";
import {
  type CSSProperties,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";

import CLIPS from "./Screenshot.clips.json";
import "./Screenshot.css";

import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/**
 * Page pixels per app pixel at most. A frame this wide or wider shows the app
 * at this scale; a narrower one shrinks it to fit, from the recording whose
 * width suits the frame best, so nothing is ever cut off.
 */
const PAGE_PX_PER_APP_PX = 0.8;
/** Page pixels of the browser title bar drawn above a ``hasBrowserFrame`` capture. */
const BROWSER_BAR_HEIGHT = 40;
/** Page pixels of browser chrome kept around the page below the title bar. */
const BROWSER_PADDING = 4;

type RecordingEvents = ConstructorParameters<typeof Replayer>[0];

/**
 * What each output replays, written by ``tests/src/marketing/clips.spec.ts``:
 * a hero scene's recordings at every breakpoint width, or a crop/component —
 * one recording and the part of it to show, in app pixels.
 */
type Clip =
  | {
      recordings: { width: number; height: number }[];
      startsAtEnd?: boolean;
    }
  | {
      recording: string;
      x: number;
      y: number;
      width: number;
      height: number;
      isolated?: boolean;
      startsAtEnd?: boolean;
    };

/** The live replay, for a caller that follows its clock. */
export type ScreenshotReplay = { replayer: Replayer };

/**
 * A product recording, replayed live: an rrweb recording of the app's DOM
 * (from ``tests/src/marketing/clips.spec.ts``), scaled to the frame. Hero
 * scenes are recorded at many window widths and the frame replays the one it
 * suits, so the app is laid out for the size it is shown at. It loads once the
 * frame nears the screen and pauses off it. Reduced-motion and data-saver
 * visitors see it held still. ``hasBrowserFrame`` sets it inside macOS-style
 * browser chrome.
 */
export function Screenshot({
  imageName,
  alt,
  hasBrowserFrame = false,
  isLooping = true,
  onEnded,
  replayRef,
}: {
  imageName: string;
  alt: string;
  hasBrowserFrame?: boolean;
  /** Start again when it finishes; otherwise hold the end and call ``onEnded``. */
  isLooping?: boolean;
  onEnded?: () => void;
  replayRef?: RefObject<ScreenshotReplay | null>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const replayerRef = useRef<Replayer | null>(null);
  const isLoopingRef = useRef(isLooping);
  const onEndedRef = useRef(onEnded);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isMissing, setIsMissing] = useState(false);

  useEffect(() => {
    isLoopingRef.current = isLooping;
    onEndedRef.current = onEnded;
  }, [isLooping, onEnded]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) =>
      setAvailableWidth(entry.contentRect.width),
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const padding = hasBrowserFrame ? BROWSER_PADDING : 0;
  const pageWidth = Math.max(availableWidth - padding * 2, 0);

  const clip = (CLIPS as unknown as Record<string, Clip>)[imageName];
  // The recording closest to showing the app at ``PAGE_PX_PER_APP_PX``.
  const view =
    "recordings" in clip
      ? (() => {
          const recording = pageWidth
            ? clip.recordings.reduce((best, other) =>
                Math.abs(
                  Math.log((other.width * PAGE_PX_PER_APP_PX) / pageWidth),
                ) <
                Math.abs(
                  Math.log((best.width * PAGE_PX_PER_APP_PX) / pageWidth),
                )
                  ? other
                  : best,
              )
            : clip.recordings[clip.recordings.length - 1];
          return {
            recording: `${imageName}-${recording.width}`,
            x: 0,
            y: 0,
            width: recording.width,
            height: recording.height,
            isolated: false,
          };
        })()
      : { isolated: false, ...clip };
  const scale = pageWidth
    ? Math.min(pageWidth / view.width, PAGE_PX_PER_APP_PX)
    : PAGE_PX_PER_APP_PX;

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;
    const isStill =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      Boolean(
        (navigator as Navigator & { connection?: { saveData?: boolean } })
          .connection?.saveData,
      );
    let isCancelled = false;
    let isLoading = false;
    let isFinished = false;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        const replayer = replayerRef.current;
        if (!entry.isIntersecting) {
          replayer?.pause();
          return;
        }
        if (replayer) {
          if (!isStill && !isFinished) replayer.play(replayer.getCurrentTime());
          return;
        }
        if (isLoading) return;
        isLoading = true;
        const loaded = await Promise.all([
          import("rrweb"),
          // Recordings are of the app in light mode, whatever the page's scheme.
          fetch(`/images/light/${view.recording}.json`).then((response) => {
            if (!response.ok) {
              throw new Error(`${response.status} ${response.url}`);
            }
            return response.json() as Promise<RecordingEvents>;
          }),
        ]).catch((error: unknown) => {
          // A recording that can't be had leaves no frame, not one that never fills.
          console.error(`Screenshot "${imageName}" failed to load`, error);
          if (!isCancelled) setIsMissing(true);
          return null;
        });
        if (!loaded || isCancelled) return;
        const [{ Replayer }, events] = loaded;
        const next = new Replayer(events, {
          root: page,
          mouseTail: false,
          skipInactive: false,
          triggerFocus: false,
          showWarning: false,
          // Report charts are recorded as canvas frames.
          UNSAFE_replayCanvas: true,
        });
        next.iframe.setAttribute("aria-hidden", "true");
        next.iframe.tabIndex = -1;
        next.on("finish", () => {
          if (isLoopingRef.current) {
            next.play(0);
          } else {
            isFinished = true;
            onEndedRef.current?.();
          }
        });
        replayerRef.current = next;
        if (replayRef) replayRef.current = { replayer: next };
        if (isStill) {
          next.pause(clip.startsAtEnd ? next.getMetaData().totalTime : 0);
        } else {
          next.play(0);
        }
        setIsReady(true);
      },
      { rootMargin: "200px" },
    );
    observer.observe(page);

    return () => {
      isCancelled = true;
      observer.disconnect();
      replayerRef.current?.destroy();
      replayerRef.current = null;
      if (replayRef) replayRef.current = null;
      setIsReady(false);
      setIsMissing(false);
    };
  }, [imageName, view.recording, clip.startsAtEnd, replayRef]);

  useEffect(() => {
    const wrapper = replayerRef.current?.wrapper;
    if (!wrapper) return;
    wrapper.style.transformOrigin = "0 0";
    wrapper.style.transform = `translate(${-view.x * scale}px, ${-view.y * scale}px) scale(${scale})`;
  }, [isReady, scale, view.x, view.y]);

  return (
    <div ref={containerRef} className={cn("w-full", isMissing && "hidden")}>
      <div
        className={cn(
          "relative w-full ml-auto mr-auto rounded-sm",
          hasBrowserFrame && "bg-muted",
          // An isolated component brings its own edges; only cut views get a frame.
          !view.isolated &&
            "[outline:1px_solid] outline-input [&::before]:[content:''] [&::before]:absolute [&::before]:top-0 [&::before]:left-0 [&::before]:right-0 [&::before]:bottom-0 [&::before]:[outline:6px_solid] [&::before]:rounded-sm [&::before]:outline-[color-mix(in_srgb,var(--accent)_30%,transparent)] [&::before]:outline-offset-1 [&::before]:pointer-events-none",
        )}
        style={{
          maxWidth: `${view.width * PAGE_PX_PER_APP_PX + padding * 2}px`,
          // The window's corners follow the page's, one padding further out.
          ...(hasBrowserFrame && {
            borderRadius: `${8 + padding}px`,
            padding: `${BROWSER_BAR_HEIGHT}px ${padding}px ${padding}px`,
          }),
        }}
      >
        {hasBrowserFrame ? (
          <div
            aria-hidden
            className="absolute top-0 left-0 right-0 h-10 flex items-center gap-2 pl-3.5 pr-3.5"
          >
            {["#FF5F57", "#FEBC2E", "#28C840"].map((color) => (
              <div
                key={color}
                className="w-3 h-3 rounded-[50%] [box-shadow:inset_0_0_0_0.5px_rgba(0,0,0,0.12)]"
                style={{ backgroundColor: color }}
              />
            ))}
            <div className="absolute left-[50%] transform-[translateX(-50%)] w-90 max-w-[calc(100%-180px)] h-6 flex items-center justify-center pl-3 pr-3 rounded-[6px] bg-card [border-width:1px] [border-style:solid] border-border">
              <Typography
                level="body-xs"
                className="truncate text-muted-foreground/80"
              >
                https://neoledger.finance
              </Typography>
            </div>
          </div>
        ) : null}
        <div
          ref={pageRef}
          role="img"
          aria-label={alt}
          data-slot="screenshot-page"
          data-ready={isReady || undefined}
          data-isolated={view.isolated || undefined}
          className={cn(
            "relative w-full overflow-hidden",
            hasBrowserFrame
              ? "rounded-[8px] [box-shadow:0_0_0_1px_var(--border)]"
              : "rounded-sm",
          )}
          style={
            {
              height: `${view.height * scale}px`,
              "--Screenshot-mouseScale": PAGE_PX_PER_APP_PX / scale,
            } as CSSProperties
          }
        >
          {isReady ? null : (
            <Skeleton
              variant="rectangular"
              className="absolute inset-0 rounded-[inherit]"
            />
          )}
        </div>
      </div>
    </div>
  );
}
